import { mapRecord } from "@base/util/TypeUtil";
import { ArticleInfo, IArticleUnits } from "@biz/articles/ArticleInfo";
import { OrderType, Unit } from "@biz/Definitions";
import { AdditionalArticleInfos, IArticle, IArticleAffiliation, ICustomArticleInfo, IShoppingArticle, OfferListType, OfferListTypeFilter, RuleArticleMap, ShoppingArticleType } from "@biz/definitions/ArticleDefinitions";
import { ArticleFeatureStore } from "@biz/definitions/ArticleFeatureDefinitions";
import { DiscountGroups } from "@biz/definitions/DatabaseDefinitions";
import { ICurrencyData, PriceListIndex } from "@biz/definitions/PriceDefinitions";
import { ILocaContext } from "@biz/language/LocaContext";
import { CalcArticlesHelper } from "@biz/logic/CalcArticlesHelper";
import { ArticleGroup, IPartListProperties } from "@biz/models/ShoppingListDefinitions";
import { PriceCalculator } from "@biz/PriceCalculator";
import { QuoteHelper } from "@biz/SSR/QuoteHelper";

export interface IShoppingCartComponentSubSection {
  id: string;
  sectionName: React.ReactNode | string;
  calculatedArticles: ICalculatedArticlePrices[];
}
export interface IShoppingCartComponentSection {
  id: string;
  productId: string;
  sectionName: React.ReactNode | string;
  subSections: IShoppingCartComponentSubSection[];
}

export interface ICalculatedPrices {
  originalArticlePrice: number;
  discountPercent: number;
  dealerPrice: number;
  customerPrice: number;
  ek: number;
  vk: number;
  inflationSurcharge: number;
}

export const sortArticleByShoppingCartPriority: (a: ICalculatedArticlePrices, b: ICalculatedArticlePrices) => number = (a, b) => {
  if (a.shoppingCartPriority || b.shoppingCartPriority) {
    return (a.shoppingCartPriority || 0) - (b.shoppingCartPriority || 0);
  }
  return 0
}

export const sortArticleEK: (a: ICalculatedArticlePrices, b: ICalculatedArticlePrices) => number = (a, b) => {
  const op = sortArticleByShoppingCartPriority(a, b);
  if (op !== 0)
    return op;
  return b.ek - a.ek
}

export const sortArticleVK: (a: ICalculatedArticlePrices, b: ICalculatedArticlePrices) => number = (a, b) => {
  const op = sortArticleByShoppingCartPriority(a, b);
  if (op !== 0)
    return op;
  return b.vk - a.vk
}

export interface IResultPrices {
  price: number;
  inflationSurcharge: number;
}

export interface ICalculatedArticlePrices extends ICalculatedPrices {
  // article info
  id: string;
  articleNumber: string;
  stkUnit: Unit;
  mulUnit: Unit;
  customArticle?: ICustomArticleInfo;
  // amount
  countDealer: number;
  countCustomer: number;
  mul: number | undefined;
  priceListIndex: PriceListIndex;
  // variants
  affiliation: IArticleAffiliation |undefined; // affiliation => keys
  features: ArticleFeatureStore;
  articleType: ShoppingArticleType;
  offerList: OfferListType;
  shoppingCartPriority?: number;
  asterisk: boolean;
  partList?: IPartListProperties;
  included?: Record<string, { articleNumber: string, count: number, mul?: number, articleGroup?: ArticleGroup; }>
  articleGroup?: ArticleGroup;
  additionalArticleInfo?: AdditionalArticleInfos;
  maxUnits: number;
  granularity: number;
}

export class OfferCalculationHelper {
  static calcPricesOnly(
    articlePrice: number,
    countDealer: number,
    countCustomer: number,
    mul: number | undefined,
    discountValue: number,
    customArticle: ICustomArticleInfo | undefined,
    currencyData: ICurrencyData,
    customerSurCharge: number,
    inflationSurcharge: number
  ): ICalculatedPrices {
    const dealerPrice = PriceCalculator.getDealerPriceDirect(articlePrice, discountValue, !!customArticle, currencyData);
    const ek = PriceCalculator.calcAmountPrice(dealerPrice, countDealer, mul, currencyData);
    const customerPrice = PriceCalculator.getCustomerPrice(articlePrice, customerSurCharge, currencyData);
    const vk = PriceCalculator.calcAmountPrice(customerPrice, countCustomer, mul, currencyData);

    const infSur = ek * inflationSurcharge / 100;
    return {
      discountPercent: discountValue,
      originalArticlePrice: articlePrice,
      dealerPrice,
      ek,
      customerPrice,
      vk,
      inflationSurcharge: customArticle ? 0 : infSur // PriceCalculator.calcAmountPrice(infSur, 1, 1, currencyData)
    };
  }

  static calcArticlePrices(article: IShoppingArticle, beosArticle: IArticle, discounts: DiscountGroups | undefined, customerSurCharge: number, inflationSurcharge: number, currencyData: ICurrencyData, orderType: OrderType | undefined): ICalculatedArticlePrices {
    const { articlePrice, mul, articleNumber, customArticle } = article;
    const countDealer = article.count - (article.countClientAvailable || 0);
    const countCustomer = article.count;

    let stkUnit: Unit | undefined;
    let mulUnit: Unit = "";
    if (article.customArticle) {
      stkUnit = article.customArticle.unit as Unit;
    } else {
      const units = ArticleInfo.calcUnits(article.articleNumber, article)
      stkUnit = units.stkUnit;
      mulUnit = units.mulUnit;
    }

    const discountGroup = beosArticle?.discountGroup !== undefined ? beosArticle.discountGroup : "";

    const discountValue = PriceCalculator.getSalesDiscount(discountGroup, discounts);
    const calculatedPrices = this.calcPricesOnly(articlePrice, countDealer, countCustomer, mul, discountValue, customArticle, currencyData, customerSurCharge, CalcArticlesHelper.isFrachtArticle(articleNumber) ? 0 : inflationSurcharge);

    return {
      id: article.id,
      articleNumber,
      customArticle,
      stkUnit,
      mulUnit,
      countCustomer,
      countDealer,
      mul,
      priceListIndex: article.priceListIndex,
      ...calculatedPrices,
      affiliation: article.affiliation,
      features: article.features || {},
      articleType: article.articleType,
      partList: article.partList,
      offerList: article.offerList || orderType as OfferListType,
      shoppingCartPriority: article.shoppingCartPriority,
      included: article.included ? mapRecord(article.included, (a) => {
        return { articleNumber: a.articleNumber, count: a.count, mul: a.mul, stkUnit: a.stkUnit, mulUnit: a.mulUnit, articleGroup: a.articleGroup }
      }) : undefined,
      articleGroup: article.articleGroup,
      additionalArticleInfo: article.additionalArticleInfo,
      granularity: 1,
      maxUnits: Infinity, // todo
      asterisk: article.offerList === "S" && orderType === "H"
    };
  }

  static calcArticlePricesFromArray(
    articles: IShoppingArticle[],
    articleMap: Record<string, IArticle>,
    discountGroups: DiscountGroups,
    customerSurCharge: number,
    inflationSurcharge: number,
    currencyData: ICurrencyData,
    orderType: OrderType,
    filter: OfferListTypeFilter = { H: true, S: true }
  ): ICalculatedArticlePrices[] {
    const calculatedArticles = articles.map((a) => OfferCalculationHelper.calcArticlePrices(a, articleMap[a.articleNumber], discountGroups, customerSurCharge, inflationSurcharge, currencyData, orderType)) || [];
    return calculatedArticles.filter((a) => !a.offerList || filter[a.offerList]);
  }

  static calcTotalFromSubSections(sections: IShoppingCartComponentSubSection[]): IResultPrices {
    const final: IResultPrices = { price: 0, inflationSurcharge: 0 }
    for (const s of sections) {
      const ret = OfferCalculationHelper.calcTotal(s.calculatedArticles);
      final.price += ret.price;
      final.inflationSurcharge += ret.inflationSurcharge;
    }
    return final;
  }

  static calcTotal(articles: ICalculatedArticlePrices[]): IResultPrices {
    let price = 0;
    let inflationSurcharge = 0;
    for (const a of articles) {
      price += a.vk;
      inflationSurcharge += a.inflationSurcharge;
    }

    return {
      price,
      inflationSurcharge,
    }
  }

  static calcArticleUnits(article: { articleNumber: string, stkUnit?: Unit, mulUnit?: Unit, customArticle?: ICustomArticleInfo }): IArticleUnits {
    if (article.customArticle) {
      return {
        stkUnit: (article.customArticle.unit as Unit) || "Stk",
        mulUnit: ""
      }
    } else {
      return ArticleInfo.calcUnits(article.articleNumber, article)
    }
  }

  static calcAmountString(prices: ICalculatedArticlePrices, forEndUser: boolean, lc: ILocaContext): string {
    const count = forEndUser ? prices.countCustomer : prices.countDealer;
    return OfferCalculationHelper.calcAmountStringNew(count, prices.mul, OfferCalculationHelper.calcArticleUnits(prices), lc);
  }

  static calcAmountStringNew(count: number, mul: number | undefined, units: IArticleUnits, lc: ILocaContext): string {
    let amountString = "";

    const mulNumber = mul ?? 1;

    if (units.mulUnit && units.mulUnit === units.stkUnit) {
      amountString = QuoteHelper.getAmount(count * mulNumber, units.stkUnit, lc);
    } else if ((mulNumber !== 1 || units.mulUnit)) {
      if (count === 1)
        amountString = QuoteHelper.getAmount(mulNumber, units.mulUnit, lc);
      else
        amountString = QuoteHelper.getAmount(count, units.stkUnit, lc) + " x " + QuoteHelper.getAmount(mulNumber, units.mulUnit, lc);
    } else {
      amountString = QuoteHelper.getAmount(count, units.stkUnit, lc);
    }
    return amountString;
  }

  static calculateArticles(articles: RuleArticleMap, articleMap: Record<string, IArticle>, discounts: DiscountGroups, customerSurCharge: number, inflationSurcharge: number, currency: ICurrencyData, orderType: OrderType, filter?: OfferListTypeFilter): ICalculatedArticlePrices[] {
    return OfferCalculationHelper.calculateShoppingArticles(CalcArticlesHelper.ruleToShoppingArticles(articles), articleMap, discounts, customerSurCharge, inflationSurcharge, currency, orderType, filter);
  }

  static calculateShoppingArticles(articles: IShoppingArticle[], articleMap: Record<string, IArticle>, discounts: DiscountGroups, customerSurCharge: number, inflationSurcharge: number, currency: ICurrencyData, orderType: OrderType, filter?: OfferListTypeFilter): ICalculatedArticlePrices[] {
    const filteredArticles = CalcArticlesHelper.filterArticles(articles);
    const calculatedArticles = OfferCalculationHelper.calcArticlePricesFromArray(filteredArticles, articleMap, discounts, customerSurCharge, inflationSurcharge, currency, orderType, filter);
    return calculatedArticles;
  }
}
